import { Modal } from '@fortress-technology-solutions/fortress-component-library/Molecules_Fortress';
import messages from './messages';
import rentForecastMessages from '../messages';
import useIntl from '../../../hooks/useIntl';
import {
  Accordion,
  Box,
  Stack,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { Checkbox } from '@fortress-technology-solutions/fortress-component-library/Molecules';
import { FileDescriptionIcon } from '@fortress-technology-solutions/fortress-component-library/Icons';
import { blue } from '@fortress-technology-solutions/fortress-component-library/design';
import { FormattedMessage } from 'react-intl';
import SignatureForm from './SignatureForm';
import useAccordionState from './hooks.useAccordionState';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import useSelectedProperty from '../../../hooks/useSelectedProperty';
import { createOrUpdate, useRdBudgetSignatureQuery } from './hooks.useQuery';
import { toastr } from 'react-redux-toastr';
import AccordionHeader from './AccordionHeader';

type Props = {
  open: boolean,
  closeModal: () => void,
  currentRentForecast: any,
};

const AuthorizeSignatureModal = ({
  open,
  closeModal,
  currentRentForecast,
}: Props) => {
  const intl = useIntl();
  const property = useSelectedProperty();
  const accordionState = useAccordionState();
  const [submitting, setSubmitting] = useState(false);
  const [formState, setFormState] = useState({
    reportSignatureData: null,
    reportPrintedName: '',
    letterSignatureData: null,
    letterPrintedName: '',
    letterAsReport: false,
  });
  const { data, refetch } = useRdBudgetSignatureQuery(
    property.organizationId,
    property.id,
    currentRentForecast?.id,
  );

  const commonAccordionProps = useMemo(
    () => ({
      AccordionDetailsProps: {
        sx: {
          padding: '8px 0 16px',
        },
      },
      AccordionSummaryProps: {
        sx: {
          padding: '0',
        },
      },
    }),
    [],
  );

  useEffect(() => {
    if (!open) return;

    refetch();
  }, [open]);

  useEffect(() => {
    if (!data) {
      return;
    }

    setFormState({
      reportSignatureData: data.reportSignatureData,
      reportPrintedName: data.reportPrintedName,
      letterSignatureData: data.letterSignatureData,
      letterPrintedName: data.letterPrintedName,
      letterAsReport: data.letterAsReport,
    });
  }, [data]);

  const onReportSignatureFormChange = useCallback((reportData) => {
    setFormState((prev) => ({
      ...prev,
      reportSignatureData: reportData.signature,
      reportPrintedName: reportData.printedName,
    }));
  }, []);

  const onLetterSignatureFormChange = useCallback((letterData) => {
    setFormState((prev) => ({
      ...prev,
      letterSignatureData: letterData.signature,
      letterPrintedName: letterData.printedName,
    }));
  }, []);

  const onLetterCheckboxChange = useCallback((evt, checked) => {
    setFormState((prev) => ({
      ...prev,
      // Clear letter fields only if letterAsReport is checked
      ...(checked ? { letterSignatureData: null, letterPrintedName: '' } : {}),
      letterAsReport: checked,
    }));
  }, []);

  const sendData = async () => {
    setSubmitting(true);

    const data = { ...formState };

    try {
      await createOrUpdate(
        property.organizationId,
        property.id,
        currentRentForecast?.id,
        data,
      );

      setFormState(data);

      toastr.success(
        intl.formatMessage(rentForecastMessages.success),
        intl.formatMessage(messages.saveSuccess),
      );
    } catch (error) {
      console.error(error);
      toastr.error(
        intl.formatMessage(rentForecastMessages.error),
        intl.formatMessage(messages.saveFailed),
      );
    }

    setSubmitting(false);
  };

  return (
    <Modal
      open={open}
      onClose={closeModal}
      title={intl.formatMessage(messages.authorizeSignatureTitle)}
      actionsProps={[
        {
          children: intl.formatMessage(rentForecastMessages.cancel),
          onClick: closeModal,
        },
        {
          children: intl.formatMessage(rentForecastMessages.save),
          onClick: sendData,
          submitButton: true,
          isSubmitting: submitting,
          disabled: currentRentForecast?.approvedFrom,
        },
      ]}
      ModalProps={{
        PaperProps: {
          sx: {
            width: {
              xs: 'calc(100% - 32px)',
              sm: 540,
            },
            maxHeight: 'calc(100vh - 100px)',
          },
        },
      }}
    >
      <Accordion
        expanded={accordionState.report[0]}
        onChange={accordionState.report[1]}
        summary={
          <Stack direction="row" spacing={2}>
            <AccordionHeader
              title="3560-7"
              icon={
                <FileDescriptionIcon
                  sx={{
                    color: blue.dark,
                    height: '1.5em',
                    width: '1.5em',
                  }}
                />
              }
              backgroundColor={blue.lighter}
            />
          </Stack>
        }
        sx={{
          borderBottom: 0,
          borderRadius: '0 !important',
        }}
        {...commonAccordionProps}
      >
        <SignatureForm
          onChange={onReportSignatureFormChange}
          signatureData={formState.reportSignatureData}
          inputValue={formState.reportPrintedName}
          disabled={currentRentForecast?.approvedFrom || submitting}
        />
      </Accordion>
      <Accordion
        expanded={accordionState.letter[0]}
        onChange={accordionState.letter[1]}
        summary={
          <Stack direction="row" spacing={2}>
            <AccordionHeader
              title="Resident Letter"
              icon={
                <FileDescriptionIcon
                  sx={{
                    color: blue.dark,
                    height: '1.5em',
                    width: '1.5em',
                  }}
                />
              }
              backgroundColor={blue.lighter}
            />
          </Stack>
        }
        sx={{
          borderRadius: '0 !important',
        }}
        {...commonAccordionProps}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
          }}
          mb={1}
        >
          <Typography variant="labelLarge" gutterBottom={true} mb={1}>
            <FormattedMessage {...messages.residentLetterLabel} />
          </Typography>
          <Checkbox
            label={intl.formatMessage(messages.residentLetterReport)}
            checked={formState.letterAsReport}
            onChange={onLetterCheckboxChange}
            disabled={currentRentForecast?.approvedFrom || submitting}
          />
        </Box>
        <SignatureForm
          onChange={onLetterSignatureFormChange}
          signatureData={formState.letterSignatureData}
          inputValue={formState.letterPrintedName}
          disabled={
            currentRentForecast?.approvedFrom || formState.letterAsReport
          }
        />
      </Accordion>
    </Modal>
  );
};

export default AuthorizeSignatureModal;
